import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
    CheckCircle2,
    Download,
    Eye,
    FileDown,
    FileSignature,
    Link2,
    Loader2,
    Lock,
    MoreHorizontal,
    PenLine,
    Printer,
    RotateCcw,
    ShieldCheck,
    TriangleAlert,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { captureError } from '@/lib/logger';
import { openSignedUrl } from '@/lib/openSignedUrl';
import { formatBusinessDate, formatCurrency } from '@/lib/format';

import AgreementReviewSheet from '@/components/agreements/AgreementReviewSheet';
import CountersignDialog from '@/components/agreements/CountersignDialog';
import MergePreflight from '@/components/agreements/MergePreflight';
import PartyChips from '@/components/agreements/PartyChips';
import SigningLinkDialog from '@/components/agreements/SigningLinkDialog';
import VoidReissueDialog from '@/components/agreements/VoidReissueDialog';
import { documentsAreOut } from '@/components/agreements/boardSlots';
import { useAgreementSealingWithFallback } from '@/components/agreements/useAgreementSealing';
import {
    countersignAgreement,
    createAgreement,
    fetchAgreementTokens,
    fetchDepositAgreement,
    issueSigningToken,
    previewAgreementPdf,
    renderAgreement,
    revokeToken,
    saveBlob,
    sweepOrphans,
    voidAgreement,
    type DepositorIdentity,
    type IssuedToken,
} from '@/lib/agreements/api';
import {
    COUNTERSIGN_CONSENT_VERSION,
    DEPOSIT_PARTY_LABELS,
    parseAgreementError,
} from '@/lib/agreements/constants';
import { liveTokenFor } from '@/lib/agreements/tokens';

import CreateDepositAgreementDialog from './CreateDepositAgreementDialog';
import { depositAgreementState, depositDriverName } from './depositAgreement';
import type { DepositSubject } from './types';

/** How often the block re-reads while the depositor's signature or the seal is outstanding. */
const POLL_INTERVAL_MS = 10_000;

interface DepositAgreementBlockProps {
    /**
     * THE PROP IS THE NARROW SHAPE, NOT THE DEPOSITS PAGE'S ROW. Both surfaces that mount this
     * block read deposits from different queries; `DepositSubject` is the intersection the block
     * actually uses, so the compiler — not a comment — keeps the two readings in agreement.
     */
    deposit: DepositSubject;
    /**
     * Where a merge-field deep link goes.
     *
     * ON THE SETUP PAGE A HARD NAVIGATE WOULD BE SELF-NAVIGATION — the block is already mounted
     * inside the page the link points at, and reloading the route would throw away every open
     * draft on it. The deposits page has nowhere to route to in-place, so it keeps the fallback.
     */
    onDeepLink?: (area: string) => void;
}

/**
 * The deposit agreement, managed from the deposit itself.
 *
 * THIS IS THE SURFACE THAT DOES NOT NEED A CONTRACT. Deposits are routinely paid weeks
 * before a vehicle is allocated — the depositor has no account, the deposit has no contract,
 * and the contract setup board (which is organised entirely by contract) cannot reach them.
 * So the block reads the deposit's own arc, takes the depositor's identity from what an admin
 * types, and puts the signature in through a shared link.
 *
 * IT IS THE SAME DOCUMENT THE BOARD SHOWS. Once the deposit is attached to a contract, both
 * surfaces read the same `contract_deposit_agreements` row — one through the arc, one through
 * `get_agreement_documents`, which reaches deposit-arc documents through the same join. There
 * is no second document and no write that reconciles them.
 */
export default function DepositAgreementBlock({ deposit, onDeepLink }: DepositAgreementBlockProps) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [creating, setCreating] = useState(false);
    const [reviewing, setReviewing] = useState(false);
    const [countersigning, setCountersigning] = useState(false);
    const [voiding, setVoiding] = useState(false);
    const [linkOpen, setLinkOpen] = useState(false);
    const [issuedToken, setIssuedToken] = useState<IssuedToken | null>(null);
    const [exporting, setExporting] = useState(false);

    const depositId = deposit.id;

    const { data: agreement, isLoading } = useQuery({
        queryKey: ['deposit-agreement', depositId],
        queryFn: () => fetchDepositAgreement(depositId),
        enabled: !!depositId,
        // Polls only while something is genuinely out: an unsigned depositor, or a completed
        // document whose executed PDF is not stored yet.
        refetchInterval: (query) =>
            documentsAreOut(query.state.data?.document ? [query.state.data.document] : [])
                ? POLL_INTERVAL_MS
                : false,
    });

    const document = agreement?.document ?? null;
    const documentId = document?.id ?? null;

    /**
     * The render is what tells this block whether the wording can go out at all (`missing[]`),
     * what hash the countersignature attests to, and where the executed PDF sits. It is keyed
     * exactly as the board keys it, so the review sheet below shares the one cache entry —
     * which is what makes "the hash on screen" and "the hash signed" the same value.
     */
    const { data: render } = useQuery({
        queryKey: ['agreement-render', documentId],
        queryFn: () => renderAgreement(documentId as number),
        enabled: !!documentId,
    });

    const { data: tokens } = useQuery({
        queryKey: ['agreement-tokens', 'deposit', depositId, documentId],
        queryFn: () => fetchAgreementTokens([documentId as number]),
        enabled: !!documentId,
    });

    const { data: signingProfile } = useQuery({
        queryKey: ['my-signing-profile'],
        queryFn: async () => {
            const { data: auth } = await supabase.auth.getUser();
            if (!auth?.user) return null;
            const { data, error } = await (supabase as any)
                .from('profiles')
                .select('id, first_name, last_name, signature_path, admins(role)')
                .eq('user_id', auth.user.id)
                .maybeSingle();
            if (error) throw error;
            return data;
        },
        staleTime: 60_000,
    });

    const signerName = signingProfile
        ? `${signingProfile.first_name ?? ''} ${signingProfile.last_name ?? ''}`.trim() || null
        : null;
    const signerRole = Array.isArray(signingProfile?.admins)
        ? signingProfile?.admins[0]?.role ?? null
        : signingProfile?.admins?.role ?? null;

    const refresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['deposit-agreement', depositId] });
        queryClient.invalidateQueries({ queryKey: ['agreement-tokens', 'deposit', depositId] });
        if (documentId) {
            queryClient.invalidateQueries({ queryKey: ['agreement-render', documentId] });
        }
        if (deposit.contract_id != null) {
            queryClient.invalidateQueries({ queryKey: ['agreement-documents', deposit.contract_id] });
            queryClient.invalidateQueries({ queryKey: ['agreement-document-arcs', deposit.contract_id] });
        }
    }, [queryClient, depositId, documentId, deposit.contract_id]);

    /**
     * THE SEAL, SHARED WHERE THERE IS SOMETHING TO SHARE IT WITH.
     *
     * On the deposits page nothing provides the context, so this is a private latch and the
     * block behaves exactly as it always has. Mounted inside a surface that already seals the
     * same documents — the signing board — it joins that latch instead of firing a second
     * finalize the first one is still waiting on.
     */
    const sealDocuments = useMemo(() => (document ? [document] : null), [document]);
    const sealing = useAgreementSealingWithFallback({
        documents: sealDocuments,
        onRefresh: refresh,
    });
    const sealAttempt = sealing.attemptFor(documentId);

    const state = useMemo(
        () =>
            depositAgreementState({
                contractId: deposit.contract_id,
                driverName: depositDriverName(deposit),
                driverPhone: deposit.driver_phone,
                arc: agreement?.arc ?? null,
                document,
                missing: render?.missing ?? null,
                sealAttempt,
            }),
        [deposit, agreement?.arc, document, render?.missing, sealAttempt],
    );

    const fail = useCallback((error: unknown) => {
        captureError(error);
        toast.error(parseAgreementError(error).message);
    }, []);

    // ── Mutations ────────────────────────────────────────────────────────────
    const generate = useMutation({
        mutationFn: (depositor: DepositorIdentity) => createAgreement('DEPOSIT', depositId, depositor),
        onSuccess: (result) => {
            toast.success(result.created ? 'Deposit agreement generated' : 'Document already exists');
            setCreating(false);
            refresh();
        },
        onError: fail,
    });

    const issue = useMutation({
        // NO HASH, AND ON PURPOSE. The depositor is the first signer, so nothing is frozen yet;
        // `agreement_token_sign` freezes against the hash the depositor's own browser computed
        // over the text their own ceremony rendered. An admin's hash, taken days earlier, is not
        // evidence about what that person read.
        mutationFn: (id: number) =>
            issueSigningToken({ documentId: id, party: 'DRIVER', contentSha256: null }),
        onSuccess: (token) => {
            setIssuedToken(token);
            refresh();
        },
        onError: fail,
    });

    const revoke = useMutation({
        mutationFn: (tokenId: number) => revokeToken(tokenId, 'Revoked from the deposit sheet'),
        onSuccess: () => {
            toast.success('Signing link revoked');
            setIssuedToken(null);
            refresh();
        },
        onError: fail,
    });

    const countersign = useMutation({
        mutationFn: (typedName: string) =>
            countersignAgreement({
                documentId: documentId as number,
                contentSha256: render!.content_sha256,
                typedName,
                signaturePath: signingProfile?.signature_path as string,
                consentVersion: COUNTERSIGN_CONSENT_VERSION,
            }),
        onSuccess: () => {
            toast.success('Countersigned');
            setCountersigning(false);
            refresh();
        },
        onError: fail,
    });

    const voidDocument = useMutation({
        mutationFn: async ({ reason, reissue }: { reason: string; reissue: boolean }) => {
            await voidAgreement(documentId as number, reason || null);
            // THE REISSUE CARRIES THE IDENTITY FORWARD, AND REFUSES ONLY WHERE THE REPLACEMENT
            // WOULD BE UNUSABLE.
            //
            // On an UNATTACHED deposit the depositor's name and phone exist nowhere but on the
            // document being voided: `agreement_create` raises AGREEMENT_SIGNER_REQUIRED without
            // the name, and the deposit template's required `driver.phone` would leave the
            // replacement printing a blank line it could never be sent with. So both are
            // insisted on there.
            //
            // ON AN ATTACHED DEPOSIT NEITHER IS INSISTED ON, BECAUSE THE RPC DOES NOT. The
            // contract answers for the signer — where its driver holds an account
            // `agreement_create` ignores these parameters outright and binds the seat to that
            // profile — and a document generated from the signing board carries no frozen
            // signer_name at all until somebody signs it. Demanding one here refused a reissue
            // SQL would have performed.
            //
            // THE ADDRESS TRAVELS WITH THEM AND IS NOT INSISTED ON. It is carried forward
            // wherever the voided document holds one, because on an unattached deposit that
            // document is the only record of it. It is NOT added to the refusal above: no
            // published template names `driver.address` yet, so a document created before the
            // address was captured at all carries none — and refusing to reissue those would
            // strand every deposit agreement that predates this field behind an error the admin
            // cannot clear.
            if (reissue) {
                const name = state.depositor?.signer_name?.trim() || null;
                const phone = agreement?.depositorPhone?.trim() || null;
                const address = agreement?.depositorAddress?.trim() || null;
                if (!state.attachedToContract && (!name || !phone)) {
                    throw new Error(
                        'This document does not carry the depositor’s name and phone, so it cannot ' +
                        'be reissued automatically. Create the agreement again and enter them.',
                    );
                }
                await createAgreement('DEPOSIT', depositId, name ? { name, phone, address } : null);
            }
        },
        onSuccess: (_result, variables) => {
            toast.success(variables.reissue ? 'Voided and reissued' : 'Document voided');
            setVoiding(false);
            refresh();
            // The bytes this withdrawal stranded [D-B44] — and this is the door that actually
            // stranded the two the production ledger still carries. Fire and forget: it never
            // throws, reports nothing, and the next void's sweep drains whatever this one missed.
            sweepOrphans();
        },
        onError: fail,
    });

    const retrySeal = () => {
        if (!documentId) return;
        sealing.retrySeal(documentId);
    };

    // ── Actions that are not mutations ───────────────────────────────────────
    const exportPdf = async () => {
        if (!documentId || !document) return;
        setExporting(true);
        try {
            saveBlob(await previewAgreementPdf(documentId, false), `${document.reference_no}-preview.pdf`);
        } catch (error) {
            fail(error);
        } finally {
            setExporting(false);
        }
    };

    /**
     * The executed PDF's location comes from `agreement_render`, which derives it server-side.
     * Re-deriving `generated/deposit/<deposit>/<document>/final.pdf` here would be a second
     * copy of a rule that has already moved once.
     */
    const downloadFinal = async () => {
        if (!render?.final_bucket || !render.final_path) return;
        const { data, error } = await supabase.storage
            .from(render.final_bucket)
            .createSignedUrl(render.final_path, 3600);
        if (error || !data?.signedUrl) {
            fail(error ?? new Error('Executed copy unavailable'));
            return;
        }
        window.open(data.signedUrl, '_blank');
    };

    /**
     * A merge field is filled in on the contract setup page, which only exists for a deposit
     * that has one. An unattached deposit's fields resolve from the deposit itself, so there
     * is nowhere to send the admin — saying so beats a link that goes nowhere.
     */
    const deepLink = (area: string) => {
        if (deposit.contract_id == null) {
            toast.info('This deposit has no contract yet — its details come from the deposit record.');
            return;
        }
        if (onDeepLink) {
            onDeepLink(area);
            return;
        }
        navigate(`/dashboard/contracts/${deposit.contract_id}/setup/${area}`);
    };

    const activeToken = liveTokenFor(tokens, documentId, 'DRIVER');
    const { seal } = state;

    if (isLoading) {
        return (
            <div className="space-y-2">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-24 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Deposit Agreement
            </p>

            <div className="rounded-xl border p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <FileSignature className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <h3 className="font-medium">{document?.title ?? 'Deposit Agreement'}</h3>
                            {state.scanned ? (
                                <Badge variant="outline" className="gap-1 font-normal">
                                    <Printer className="h-3 w-3" />
                                    Paper
                                </Badge>
                            ) : (
                                <Badge variant="outline" className="gap-1 font-normal">
                                    <Link2 className="h-3 w-3" />
                                    Signed by link
                                </Badge>
                            )}
                        </div>
                        {document && (
                            <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                                {document.reference_no}
                            </p>
                        )}
                        {/* THE CONTRACT IS REPORTED, NEVER WAITED FOR. A deposit paid before a
                            vehicle was allocated has no contract to name and this agreement does
                            not name one — so the line says what is true today rather than dressing
                            the ordinary case up as something missing. Assignment stamps the
                            contract onto the document server-side; nothing here has to.

                            THE REQUIREMENT IS NAMED ONLY WHERE ACTIVATION EXISTS [D-B15]. This
                            block is mounted on the deposits page too, where a deposit routinely has
                            no contract for weeks — telling that admin the paper is "required to
                            activate" would name a gate their deposit has not reached. Attached, it
                            is exactly the gate `sign_contract` refuses on.

                            AND THE GATE IS OVER THE PAPER, NEVER OVER THE MONEY [D-B41]. A contract
                            that took no deposit is activated without one; this line is drawn beside
                            a deposit that exists, which is the only case [D-B15] was ever about. */}
                        <p className="mt-1 text-xs text-muted-foreground">
                            {state.attachedToContract ? (
                                <>
                                    <button
                                        onClick={() => navigate(`/dashboard/contracts/${deposit.contract_id}`)}
                                        className="text-primary hover:underline"
                                    >
                                        Contract #{deposit.contract_id}
                                    </button>
                                    {' · required before that contract can be activated'}
                                </>
                            ) : (
                                'Not attached to a contract yet — this agreement names the depositor, not a contract.'
                            )}
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        {state.primary === 'create' && (
                            <Button size="sm" onClick={() => setCreating(true)}>
                                <FileSignature className="mr-2 h-4 w-4" />
                                Create agreement
                            </Button>
                        )}
                        {state.primary === 'countersign' && (
                            <Button size="sm" onClick={() => setCountersigning(true)}>
                                <PenLine className="mr-2 h-4 w-4" />
                                Sign as Primelane
                            </Button>
                        )}
                        {state.primary === 'seal' && (
                            <Button
                                size="sm"
                                variant={seal.phase === 'failed' ? 'default' : 'outline'}
                                onClick={retrySeal}
                                disabled={seal.phase === 'sealing'}
                            >
                                {seal.phase === 'sealing' ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <ShieldCheck className="mr-2 h-4 w-4" />
                                )}
                                {seal.phase === 'sealing'
                                    ? 'Finalizing'
                                    : seal.phase === 'failed'
                                      ? 'Retry finalize'
                                      : 'Finalize'}
                            </Button>
                        )}
                        {state.primary === 'link' && (
                            <Button
                                size="sm"
                                onClick={() => {
                                    setIssuedToken(null);
                                    setLinkOpen(true);
                                }}
                            >
                                <Link2 className="mr-2 h-4 w-4" />
                                Copy signing link
                            </Button>
                        )}

                        {document && (
                            <Button variant="ghost" size="sm" onClick={() => setReviewing(true)}>
                                <Eye className="mr-2 h-4 w-4" />
                                Review
                            </Button>
                        )}

                        {/* NO TRIGGER WITHOUT AN ITEM BEHIND IT — the row above's rule, on the
                            same shape. `canIssueLink` is `!!doc && …` and every other entry is
                            gated on the document too, so a deposit with no agreement raised opened
                            an empty popover. */}
                        {(!!document || state.canIssueLink) && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" aria-label="More agreement actions">
                                        <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-64">
                                    {state.canIssueLink && (
                                        <DropdownMenuItem
                                            onClick={() => {
                                                setIssuedToken(null);
                                                setLinkOpen(true);
                                            }}
                                        >
                                            <Link2 className="mr-2 h-4 w-4" />
                                            {activeToken ? 'Regenerate signing link' : 'Copy signing link'}
                                        </DropdownMenuItem>
                                    )}
                                    {document && (
                                        <DropdownMenuItem onClick={exportPdf} disabled={exporting}>
                                            <FileDown className="mr-2 h-4 w-4" />
                                            Export PDF
                                        </DropdownMenuItem>
                                    )}
                                    {document?.finalized_at && (
                                        <DropdownMenuItem onClick={downloadFinal}>
                                            <Download className="mr-2 h-4 w-4" />
                                            Download executed PDF
                                        </DropdownMenuItem>
                                    )}
                                    {document && <DropdownMenuSeparator />}
                                    {document && (
                                        <DropdownMenuItem
                                            onClick={() => setVoiding(true)}
                                            className="text-destructive focus:text-destructive"
                                        >
                                            <RotateCcw className="mr-2 h-4 w-4" />
                                            Void &amp; reissue
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                </div>

                {document && (
                    <PartyChips
                        signatures={document.signatures}
                        since={document.frozen_at ?? document.created_at}
                        labels={DEPOSIT_PARTY_LABELS}
                        className="mt-3"
                    />
                )}

                {document?.frozen_at && document.status !== 'COMPLETED' && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Lock className="h-3 w-3" />
                        Document locked · content frozen{' '}
                        {format(new Date(document.frozen_at), 'MMM d, yyyy · HH:mm')}
                    </p>
                )}

                {state.scanned && (
                    <p className="mt-2 text-sm text-muted-foreground">
                        Signed{' '}
                        {agreement?.arc?.signed_at
                            ? format(new Date(agreement.arc.signed_at), 'MMM d, yyyy')
                            : ''}
                        {agreement?.arc?.agreement_url && (
                            <>
                                {' · '}
                                <button
                                    onClick={() => openSignedUrl(agreement.arc!.agreement_url)}
                                    className="text-primary hover:underline"
                                >
                                    View scan
                                </button>
                            </>
                        )}
                    </p>
                )}

                {document && state.blockedByMerge && (
                    <MergePreflight missing={render?.missing ?? []} onDeepLink={deepLink} />
                )}

                {seal.phase === 'sealing' && (
                    <div className="mt-3 flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Finalizing — sealing the signatures and generating the executed PDF.
                    </div>
                )}

                {/* VERBATIM, AND IT STAYS. The renderer names what it is missing, and only that
                    sentence tells an admin whether to fix something or just ask again. */}
                {seal.phase === 'failed' && (
                    <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                        <p className="flex items-center gap-2 font-medium">
                            <TriangleAlert className="h-4 w-4 shrink-0" />
                            Sealing failed — the executed PDF was not stored.
                        </p>
                        <p className="mt-1 pl-6">{seal.message}</p>
                        {seal.code && <p className="mt-1 pl-6 font-mono text-xs opacity-80">{seal.code}</p>}
                    </div>
                )}

                {document?.status === 'COMPLETED' && document.finalized_at && (
                    <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400">
                        <span className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4" />
                            Completed · executed PDF ready
                        </span>
                        <Button variant="ghost" size="sm" onClick={downloadFinal}>
                            <Download className="mr-2 h-4 w-4" />
                            Download
                        </Button>
                    </div>
                )}

                <p className="mt-3 text-xs text-muted-foreground">{state.statusLine}</p>
            </div>

            <CreateDepositAgreementDialog
                open={creating}
                onOpenChange={setCreating}
                attachedToContract={state.attachedToContract}
                prefill={state.depositorPrefill}
                depositSummary={`${formatCurrency(deposit.amount, deposit.currency)} · ${formatBusinessDate(deposit.transaction_at ?? deposit.created_at)}`}
                isPending={generate.isPending}
                onConfirm={(depositor) => generate.mutate(depositor)}
            />

            <AgreementReviewSheet
                documentId={reviewing ? documentId : null}
                onOpenChange={(open) => !open && setReviewing(false)}
                onDeepLink={deepLink}
                onDownloadFinal={downloadFinal}
            />

            {countersigning && document && (
                <CountersignDialog
                    open
                    onOpenChange={(open) => !open && setCountersigning(false)}
                    documentTitle={document.title}
                    referenceNo={document.reference_no}
                    signerName={signerName}
                    signerRole={signerRole}
                    signaturePath={signingProfile?.signature_path ?? null}
                    contentSha256={render?.content_sha256 ?? null}
                    isPending={countersign.isPending}
                    onConfirm={(typedName) => countersign.mutate(typedName)}
                />
            )}

            {voiding && document && (
                <VoidReissueDialog
                    open
                    onOpenChange={(open) => !open && setVoiding(false)}
                    documentTitle={document.title}
                    referenceNo={document.reference_no}
                    requiresReason={document.status === 'COMPLETED'}
                    isPending={voidDocument.isPending}
                    onConfirm={(reason, reissue) => voidDocument.mutate({ reason, reissue })}
                />
            )}

            {linkOpen && document && (
                <SigningLinkDialog
                    open
                    onOpenChange={(open) => {
                        if (!open) {
                            setLinkOpen(false);
                            setIssuedToken(null);
                        }
                    }}
                    party="DRIVER"
                    recipientName={state.depositor?.signer_name ?? depositDriverName(deposit)}
                    issued={issuedToken}
                    activeToken={activeToken}
                    isIssuing={issue.isPending}
                    isRevoking={revoke.isPending}
                    onIssue={() => issue.mutate(document.id)}
                    onRevoke={() => activeToken && revoke.mutate(activeToken.id)}
                />
            )}
        </div>
    );
}
